/**
 * api.ts —— 前端 API 薄層（fetch ＋ 錯誤正規化）
 * ⚠️ 一律用相對路徑（同源）；**唔准**喺前端放任何密鑰。
 */
export interface Me { id: string; role: 'founder' | 'cofounder' | 'accountant'; name: string; phone: string; mustChangePin: boolean }
export interface Meta {
  entities: { id: string; code: string; name_zh: string; name_en: string }[];
  accounts: { id: string; entity_id: string; kind: string; name: string; gl_code: string }[];
  projects: { id: string; code: string; name: string }[];
  vendors: { id: string; name: string }[];
  customers: { id: string; name: string; source: string }[];
  expenseCategories: { code: string; name_zh: string; name_en: string }[];
  incomeCategories: { code: string; name_zh: string; name_en: string }[];
  settings: Record<string, string>;
  paymentMethods: string[];
  paidByOptions: string[];
}
export interface VoucherRow {
  id: string; voucher_no: string; kind: string; entity_id: string; txn_date: string; period: string;
  paid_by: string; payment_method: string; amount_cents: number; purpose: string; status: string;
  reimbursement_status: string; attachments: number; project_id: string | null; doc_no: string | null;
  vendor_name: string | null; customer_name: string | null; attachment_id: string | null; gl_codes: string | null;
}
export interface VoucherDetailData {
  ok: true;
  voucher: Record<string, unknown> & {
    id: string; voucher_no: string; kind: string; txn_date: string; amount_cents: number;
    paid_by: string; payment_method: string; purpose: string; status: string; reimbursement_status: string;
  };
  lines: { line_no: number; gl_code: string; dr_cents: number; cr_cents: number; memo: string | null }[];
  attachments: { id: string; mime: string; bytes: number; sha256: string; kind: string }[];
  ocr: { id: string; provider: string; model: string; parsed_json: string | null; confidence: string | null }[];
  /** 我被人沖銷 → 沖銷單資料；我係沖銷單 → 原單資料 */
  reversal: { id: string; voucher_no: string; txn_date: string; purpose: string } | null;
  original: { id: string; voucher_no: string; txn_date: string; status: string } | null;
}
export interface AccountTotal { gl_code: string; name_zh: string; name_en: string; type: string; dr_cents: number; cr_cents: number; net_cents: number; n: number }
export interface GroupTotal { key: string; label: string; total_cents: number; n: number }
export interface ReimbursementSummary {
  paid_by: string; pending_cents: number; settled_cents: number; pending_count: number;
  items: { id: string; voucher_no: string; txn_date: string; amount_cents: number; purpose: string }[];
}
export interface MonthlyReport {
  ok: true;
  filter: { entityId: string | null; period: string | null };
  totals: { expense_cents: number; income_cents: number; net_cents: number; expense_count: number; income_count: number };
  byCategory: AccountTotal[];
  incomeByCategory: AccountTotal[];
  byPaidBy: GroupTotal[];
  byPaymentMethod: GroupTotal[];
  byProject: GroupTotal[];
  byMonth: GroupTotal[];
  reimbursements: ReimbursementSummary[];
  periods: { period: string; status: string }[];
  locked: boolean;
}
export interface ProfitTrial {
  ok: true;
  period: string;
  entityId: string | null;
  income_cents: number; expense_cents: number; net_cents: number;
  byEntity: { entityId: string; name: string; income_cents: number; expense_cents: number; net_cents: number; locked: boolean }[];
  split: { ai85_pct: number; ai85_cents: number; rest_cents: number };
  allocation: { party: string; pct: number; amount_cents: number }[];
  config: { parties: { name: string; pct: number }[]; source: string };
  warnings: string[];
  posting: 'disabled';
}
export interface BudgetRow {
  glCode: string; nameZh: string; nameEn: string; type: string;
  budgetCents: number; actualCents: number; varianceCents: number;
  usedPct: number | null; unbudgeted: boolean;
}
export interface BudgetReport {
  ok: true; entityId: string; period: string;
  rows: BudgetRow[];
  totals: { expenseBudget: number; expenseActual: number; incomeBudget: number; incomeActual: number };
  warnings: string[];
}
export interface OpenItem {
  id: string; entity_id: string; kind: 'ar' | 'ap'; party_type: string; party_id: string | null;
  party_name: string; doc_no: string | null; issue_date: string; due_date: string;
  amount_cents: number; settled_cents: number; balance_cents: number; status: string;
  gl_code: string; voucher_id: string; voucher_no: string; voucher_status: string; note: string | null;
}
export interface AgingBucketTotals { current: number; d1_30: number; d31_60: number; d61_90: number; d90p: number }
export interface AgingReport {
  ok: true; asOf: string; entityId: string | null;
  rows: { kind: string; party: string; count: number; balance: number; buckets: AgingBucketTotals; maxDaysOverdue: number }[];
  totals: { ar: number; ap: number; net: number };
  bucketTotals: { ar: AgingBucketTotals; ap: AgingBucketTotals };
  note: string;
}
/** 銀行對帳（P2-5；`decisions/008`）*/
export interface BankSuggestion { voucher_id: string; voucher_no: string; txn_date: string; purpose: string }
export interface BankLine {
  id: string; account_id: string; txn_date: string; description: string | null; ref: string | null;
  amount_cents: number; balance_cents: number | null; status: string;
  matched_voucher_id: string | null; ignored_reason: string | null; suggestions: BankSuggestion[];
}
export interface BankLinesResponse {
  ok: true;
  account: { id: string; name: string; glCode: string };
  lines: BankLine[];
  summary: { unmatched: number; matched: number; ignored: number; unmatchedNet: number };
}
export interface BankReport {
  ok: true;
  account: { id: string; name: string; glCode: string; entityId: string };
  asOf: string;
  statementClosingCents: number; statementClosingSource: string;
  bookBalanceCents: number; differenceCents: number;
  unmatched: {
    statement: { count: number; netCents: number; rows: { id: string; txn_date: string; description: string | null; amount_cents: number }[] };
    book: { count: number; netCents: number; rows: { voucher_id: string; voucher_no: string; txn_date: string; kind: string; purpose: string; amount_cents: number }[] };
    netDifferenceCents: number;
  };
  ignored: { count: number; rows: { id: string; txn_date: string; description: string | null; amount_cents: number; ignored_reason: string | null }[] };
}
/** 固定資產＋折舊（P2-6；`decisions/009`）*/
export interface FixedAsset {
  id: string; asset_no: string; entity_id: string; name: string; category: string | null;
  acquired_date: string; cost_cents: number; salvage_cents: number; life_months: number; method: string;
  gl_code_asset: string; gl_code_accum: string; gl_code_expense: string;
  status: 'active' | 'disposed'; disposed_date: string | null; disposed_proceeds_cents: number | null;
  disposal_voucher_id: string | null; note: string | null; created_at: string;
  accumulated_cents: number; book_value_cents: number; monthly_cents: number;
  months_posted: number; remaining_months: number; used_pct: number;
}
export interface DepreciationRow {
  assetId: string; assetNo: string; name: string;
  costCents: number; accumulatedCents: number; remainingCents: number; monthsPosted: number; amountCents: number;
}
export interface AssetsResponse {
  ok: true;
  assets: FixedAsset[];
  summary: { count: number; activeCount: number; costCents: number; accumulatedCents: number; bookValueCents: number };
  runs: { id: string; period: string; total_cents: number; asset_count: number; voucher_no: string; posted_at: string }[];
}
export interface DepreciationPreview {
  ok: true; entityId: string; period: string;
  rows: DepreciationRow[];
  totals: { count: number; amountCents: number };
  alreadyPosted: { voucherNo: string; totalCents: number } | null;
}
export interface DistributionPreview {
  ok: true; entityId: string; period: string;
  incomeCents: number; expenseCents: number; netCents: number;
  items: { code: string; name: string; pct: number; amountCents: number }[];
  debitCode: string; ai85Mode: string;
  ruleOk: boolean; ruleErrors: string[]; ruleSum: number;
  locked: boolean;
  alreadyPosted: { id: string; totalCents: number } | null;
  canPost: boolean;
}
export interface DistributionRow {
  id: string; entity_id: string; period: string; base_net_cents: number; total_cents: number;
  voucher_no: string; voucher_status: string; status: string; posted_at: string;
  lines: { target_code: string; target_name: string; pct_bp: number; amount_cents: number }[];
}
export interface VoucherFilters {
  period?: string; entityId?: string; kind?: string; paidBy?: string; paymentMethod?: string;
  projectId?: string; vendorId?: string; glCode?: string; q?: string; reimbursement?: string;
  limit?: number; offset?: number;
}
export interface EvidenceItem {
  id: string; mime: string; bytes: number; kind: string; uploaded_at: string;
  voucher_id: string; voucher_no: string; txn_date: string; period: string; voucher_kind: string;
  amount_cents: number; purpose: string; doc_no: string | null; status: string; entity_id: string;
  vendor_name: string | null; customer_name: string | null;
}
export interface EvidenceResponse {
  ok: true;
  items: EvidenceItem[];
  summary: { files: number; vouchers: number };
}
export interface Attachment { id: string; mime: string; bytes: number; sha256: string; width: number | null; height: number | null }
/** OCR 回傳（`decisions/003` 兩段式；**machineRead 一定要人手核對**）*/
export interface OcrFields {
  vendor: string | null;
  dateISO: string | null;
  amountCents: number | null;
  currency: string | null;
  docNo: string | null;
  taxCents: number | null;
  categoryHint: string | null;
  suggestedExpenseCode: string | null;
  purposeDraft: string | null;
  warnings: string[];
}
export interface OcrResult {
  ok: true;
  fields: OcrFields;
  rawText: string;
  models: { vision: string; normalize: string };
  ms: number;
  neurons: number;
  machineRead: true;
}

async function req<T>(method: string, path: string, body?: unknown, form?: FormData): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: form ? undefined : body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: form ?? (body !== undefined ? JSON.stringify(body) : undefined),
  });
  if (res.status === 401) throw new Error('UNAUTHENTICATED');
  const data = (await res.json().catch(() => null)) as { error?: string } | null;
  if (!res.ok) throw new Error(data?.error ?? `HTTP ${res.status}`);
  return data as T;
}

export const api = {
  me: () => req<{ ok: true; user: Me }>('GET', '/api/auth/me'),
  login: (phone: string, pin: string) => req<{ ok: true; user: Me }>('POST', '/api/auth/login', { phone, pin }),
  logout: () => req<{ ok: true }>('POST', '/api/auth/logout'),
  /** 改 PIN（首次登入強制改；要驗舊 PIN）*/
  changePin: (currentPin: string, newPin: string) => req<{ ok: true }>('POST', '/api/auth/change-pin', { currentPin, newPin }),
  meta: () => req<Meta & { ok: true }>('GET', '/api/meta'),
  upload: (file: Blob, filename: string, width?: number, height?: number) => {
    const f = new FormData();
    f.append('file', file, filename);
    f.append('kind', 'receipt');
    if (width) f.append('width', String(width));
    if (height) f.append('height', String(height));
    return req<{ ok: true; attachment: Attachment }>('POST', '/api/upload', undefined, f);
  },
  createVoucher: (body: Record<string, unknown>) => req<{ ok: true; id: string; voucherNo: string }>('POST', '/api/vouchers', body),
  /** 收據 OCR（第一段 vision ＋ 第二段 JSON Mode；失敗會 throw，呼叫者要顯示但**唔阻**記帳）*/
  ocrReceipt: (attachmentId: string) => req<OcrResult>('POST', '/api/ocr/receipt', { attachmentId }),
  /** 一次性 Meta license 同意（founder only）*/
  agreeOcrLicense: () => req<{ ok: true; model: string }>('POST', '/api/ocr/agree'),
  /** 紅字沖銷（唔准刪；founder／cofounder；要原因）*/
  voidVoucher: (id: string, reason: string) =>
    req<{ ok: true; originalNo: string; reversalId: string; reversalNo: string }>('POST', `/api/vouchers/${id}/void`, { reason }),
  /** 審計鏈驗算（founder only）*/
  auditVerify: () => req<{ ok: boolean; count?: number; brokenAt?: string; reason?: string; checkedAt: string }>('GET', '/api/audit/verify'),
  auditList: (limit = 50) => req<{ ok: true; entries: Record<string, unknown>[] }>('GET', `/api/audit?limit=${limit}`),
  /** 期間（月結鎖帳）*/
  periods: (entityId: string) => req<{ ok: true; periods: { period: string; entity_id: string; status: string; locked_at: string | null }[] }>(
    'GET', `/api/periods?entityId=${encodeURIComponent(entityId)}`),
  setPeriod: (entityId: string, period: string, action: 'lock' | 'unlock') =>
    req<{ ok: true; period: string; status: string }>('POST', '/api/periods', { entityId, period, action }),
  vouchers: (params: VoucherFilters = {}) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    }
    const suffix = q.toString();
    return req<{ ok: true; vouchers: VoucherRow[]; totals: { kind: string; period: string; total_cents: number; n: number }[] }>(
      'GET', `/api/vouchers${suffix ? `?${suffix}` : ''}`,
    );
  },
  voucher: (id: string) => req<VoucherDetailData>('GET', `/api/vouchers/${id}`),
  /** 供應商：查（打字搜尋）／即場新建（同名重用）*/
  vendors: (q = '') => req<{ ok: true; vendors: { id: string; name: string }[] }>('GET', `/api/vendors${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  customers: (q = '') => req<{ ok: true; customers: { id: string; name: string; source: string }[] }>('GET', `/api/customers${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  openItems: (entityId: string, status = 'open') =>
    req<{ ok: true; items: OpenItem[]; summary: { arBalance: number; apBalance: number } }>(
      'GET', `/api/open-items?entityId=${encodeURIComponent(entityId)}&status=${status}`),
  createOpenItem: (body: Record<string, unknown>) => req<{ ok: true; id: string; voucherNo: string }>('POST', '/api/open-items', body),
  settleOpenItem: (id: string, body: { amount: string; txnDate: string; accountId: string }) =>
    req<{ ok: true; voucherNo: string; settledCents: number; balanceCents: number }>('POST', `/api/open-items/${id}/settle`, body),
  aging: (entityId: string, asOf?: string) => {
    const q = new URLSearchParams({ entityId });
    if (asOf) q.set('asOf', asOf);
    return req<AgingReport>('GET', `/api/reports/aging?${q.toString()}`);
  },
  budgets: (entityId: string, period: string) =>
    req<BudgetReport>('GET', `/api/budgets?entityId=${encodeURIComponent(entityId)}&period=${encodeURIComponent(period)}`),
  setBudgets: (entityId: string, period: string, lines: { glCode: string; amountCents: number }[]) =>
    req<{ ok: true; updated: number }>('POST', '/api/budgets', { entityId, period, lines }),
  /** 淨利分配試算（P2-1；只讀，唔過帳）*/
  profit: (p: { entityId?: string; period?: string; split?: string; parties?: string }) => {
    const q = new URLSearchParams();
    if (p.entityId) q.set('entityId', p.entityId);
    if (p.period) q.set('period', p.period);
    if (p.split) q.set('split', p.split);
    if (p.parties) q.set('parties', p.parties);
    return req<ProfitTrial>('GET', `/api/reports/profit?${q.toString()}`);
  },
  /** 銀行對帳（P2-5）：匯入月結單 → 建議配對 → 對帳報告；**對帳唔會改帳** */
  bankImport: (accountId: string, csv: string) =>
    req<{ ok: true; imported: number; skipped: number; layout: string; lastBalanceCents: number | null; errors: { row: number; raw: string; reason: string }[]; errorCount: number }>(
      'POST', '/api/bank/import', { accountId, csv }),
  bankLines: (accountId: string, status = 'unmatched') =>
    req<BankLinesResponse>('GET', `/api/bank/lines?accountId=${encodeURIComponent(accountId)}&status=${status}`),
  bankAct: (body: { lineId: string; action: 'match' | 'ignore'; voucherId?: string; reason?: string }) =>
    req<{ ok: true; status: string; voucherNo?: string; reason?: string }>('POST', '/api/bank/act', body),
  bankReport: (accountId: string, asOf?: string) =>
    req<BankReport>('GET', `/api/bank/report?accountId=${encodeURIComponent(accountId)}${asOf ? `&asOf=${asOf}` : ''}`),
  /** 固定資產＋折舊（P2-6）：清單／新增／試算／過帳／處置 */
  assets: (entityId: string, status = 'all') =>
    req<AssetsResponse>('GET', `/api/assets?entityId=${encodeURIComponent(entityId)}&status=${status}`),
  createAsset: (body: Record<string, unknown>) => req<{ ok: true; asset: FixedAsset }>('POST', '/api/assets', body),
  depreciationPreview: (entityId: string, period: string) =>
    req<DepreciationPreview>('GET', `/api/assets/depreciation?entityId=${encodeURIComponent(entityId)}&period=${encodeURIComponent(period)}`),
  postDepreciation: (entityId: string, period: string) =>
    req<{ ok: true; voucherNo: string; totalCents: number; rows: DepreciationRow[] }>(
      'POST', '/api/assets/depreciation', { entityId, period }),
  disposeAsset: (id: string, body: { date: string; proceeds: string; accountId?: string; note?: string }) =>
    req<{ ok: true; voucherNo: string; bookValueCents: number; gainCents: number; warnings: string[] }>(
      'POST', `/api/assets/${id}/dispose`, body),
  /** 淨利分配（P2-2；`decisions/010`）：試算 → 過帳（一期一次、要鎖帳）*/
  distributionPreview: (entityId: string, period: string, p?: { coleadery?: string; marketing?: string; ai85?: string }) => {
    const q = new URLSearchParams({ entityId, period });
    if (p?.coleadery) q.set('coleadery', p.coleadery);
    if (p?.marketing) q.set('marketing', p.marketing);
    if (p?.ai85) q.set('ai85', p.ai85);
    return req<DistributionPreview>('GET', `/api/distributions/preview?${q.toString()}`);
  },
  distributions: (entityId: string) =>
    req<{ ok: true; entityId: string; distributions: DistributionRow[] }>(
      'GET', `/api/distributions?entityId=${encodeURIComponent(entityId)}`),
  postDistribution: (body: { entityId: string; period: string; coleadery?: number; marketing?: number; ai85?: number }) =>
    req<{ ok: true; voucherNo: string; baseNetCents: number; totalCents: number;
      items: { code: string; name: string; pct: number; amountCents: number }[] }>('POST', '/api/distributions', body),
  /** 憑證總覽（會計師／管理層）*/
  evidence: (p: { entityId?: string; period?: string; from?: string; to?: string; q?: string; limit?: number; offset?: number } = {}) => {
    const q = new URLSearchParams();
    if (p.entityId) q.set('entityId', p.entityId);
    if (p.period) q.set('period', p.period);
    if (p.from) q.set('from', p.from);
    if (p.to) q.set('to', p.to);
    if (p.q) q.set('q', p.q);
    if (p.limit) q.set('limit', String(p.limit));
    if (p.offset) q.set('offset', String(p.offset));
    return req<EvidenceResponse>('GET', `/api/evidence?${q.toString()}`);
  },
  /** 月報（P1-8；全部數字由分錄計）*/
  report: (entityId?: string, period?: string) => {
    const q = new URLSearchParams();
    if (entityId) q.set('entityId', entityId);
    if (period) q.set('period', period);
    return req<MonthlyReport>('GET', `/api/reports/monthly?${q.toString()}`);
  },
};

/** 顯示用（金額由 `packages/money` 規則：整數分 → 顯示）*/
export const fmtMoney = (cents: number): string =>
  new Intl.NumberFormat('zh-HK', { style: 'currency', currency: 'HKD', minimumFractionDigits: 2 }).format(cents / 100);

export const todayISO = (): string => new Date().toISOString().slice(0, 10);
export const currentPeriod = (): string => todayISO().slice(0, 7);

/** 匯出連結（P1-7）：直接開＝瀏覽器下載（帶 session cookie）*/
export function exportUrl(kind: 'xlsx' | 'pack', p: { entityId?: string; period?: string; from?: string; to?: string }): string {
  const q = new URLSearchParams();
  if (p.entityId) q.set('entityId', p.entityId);
  if (p.period) q.set('period', p.period);
  if (p.from) q.set('from', p.from);
  if (p.to) q.set('to', p.to);
  return `/api/export/${kind}?${q.toString()}`;
}
